import React, { useId, useState } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { ChevronDown } from "./icons";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Same blue gradient shell as company dashboard Compliance overview / For you panes. */
export const DASHBOARD_GRADIENT_PANE_SHELL =
  "relative overflow-hidden rounded-2xl border border-slate-700/80 bg-gradient-to-br from-slate-900/88 via-indigo-950/82 to-slate-900/88 p-5 text-white shadow-xl backdrop-blur-[2px] sm:p-6";

export function DashboardGradientPaneGlows() {
  return (
    <>
      <div
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-sky-400/15 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-24 -left-12 h-52 w-52 rounded-full bg-indigo-500/20 blur-3xl"
        aria-hidden
      />
    </>
  );
}

/** Inner KPI / shortcut tiles on a gradient pane. */
export const DASHBOARD_GLASS_TILE =
  "relative overflow-hidden rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur-sm transition-colors";

export const DASHBOARD_GLASS_TILE_INTERACTIVE = cn(
  DASHBOARD_GLASS_TILE,
  "hover:bg-white/[0.1] cursor-pointer"
);

export function DashboardGradientSection({
  title,
  subtitle,
  icon: Icon,
  children,
  className,
  bodyClassName,
  collapsible = false,
  defaultCollapsed = true,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(() => (collapsible ? Boolean(defaultCollapsed) : false));
  const panelId = useId();
  const expanded = collapsible ? !collapsed : true;
  const toggle = () => setCollapsed((v) => !v);

  const heading = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        {Icon ? (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-indigo-100 ring-1 ring-white/20">
            <Icon className="h-5 w-5" />
          </span>
        ) : null}
        <div className="min-w-0">
          {collapsible ? (
            <span className="block text-base font-bold tracking-tight text-white">{title}</span>
          ) : (
            <h2 className="text-base font-bold tracking-tight text-white">{title}</h2>
          )}
          {subtitle && expanded ? <p className="mt-0.5 text-xs text-slate-300">{subtitle}</p> : null}
        </div>
      </div>
      {collapsible ? (
        <span className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white ring-1 ring-white/20">
          <ChevronDown className={cn("h-5 w-5 transition-transform duration-200", expanded && "rotate-180")} />
        </span>
      ) : null}
    </>
  );

  return (
    <section
      className={cn(
        DASHBOARD_GRADIENT_PANE_SHELL,
        collapsible && !expanded && "py-3 sm:py-3",
        className
      )}
    >
      <DashboardGradientPaneGlows />
      <div className={cn("relative z-10", bodyClassName)}>
        {collapsible ? (
          <button
            type="button"
            className={cn(
              "-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-1 text-left transition",
              "hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50",
              expanded ? "mb-4" : "mb-0"
            )}
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
            onClick={toggle}
          >
            {heading}
          </button>
        ) : (
          <div className={cn("flex flex-wrap items-start gap-3", expanded ? "mb-4" : "mb-0")}>{heading}</div>
        )}
        {expanded ? <div id={panelId}>{children}</div> : null}
      </div>
    </section>
  );
}
